import React, { useState, useEffect, useContext } from 'react';
import {
  Button,
  TextField,
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Grid,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { AlertMessages } from '../AlertMessages';
import { AuthContext } from '../../contexts/Auth/AuthContext';
import { createProduct, getAllProducts, updateProduct, deleteProduct } from '../../api/endpoints/wallet';
import { getErrorMessage } from '../../utils/errorHandler';
import { formatCurrency } from '../../utils/formatCoins';
import { FormContainer } from './styles';

export const ProductForm = () => {
  const { user } = useContext(AuthContext);
  const [name, setName] = useState('');
  const [priceCoins, setPriceCoins] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [loading, setLoading] = useState(false);
  const [productsLoading, setProductsLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  const [alertState, setAlertState] = useState({
    show: false,
    type: 'success',
    message: '',
  });

  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    try {
      setProductsLoading(true);
      const data = await getAllProducts();
      setProducts(data.products || []);
    } catch (error) {
      setAlertState({
        show: true,
        type: 'error',
        message: getErrorMessage(error),
      });
    } finally {
      setProductsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!name.trim() || !priceCoins) {
      setAlertState({
        show: true,
        type: 'error',
        message: 'Preencha nome e preço',
      });
      return;
    }

    const price = parseFloat(priceCoins);
    if (isNaN(price) || price <= 0) {
      setAlertState({
        show: true,
        type: 'error',
        message: 'Preço deve ser um número positivo',
      });
      return;
    }

    try {
      setLoading(true);
      const productData = {
        name: name.trim(),
        priceCoins: price,
        isActive,
      };

      const result = await createProduct(productData);

      setAlertState({
        show: true,
        type: 'success',
        message: `Produto ${result.product.name} cadastrado com sucesso!`,
      });

      // Limpar formulário
      setName('');
      setPriceCoins('');
      setIsActive(true);

      // Recarregar lista
      await loadProducts();
    } catch (error) {
      setAlertState({
        show: true,
        type: 'error',
        message: getErrorMessage(error),
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (product) => {
    setEditingProduct(product);
    setName(product.name);
    setPriceCoins(product.priceCoins.toString());
    setIsActive(product.isActive);
    setEditDialogOpen(true);
  };

  const handleEditCancel = () => {
    setEditDialogOpen(false);
    setEditingProduct(null);
    setName('');
    setPriceCoins('');
    setIsActive(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();

    if (!name.trim() || !priceCoins) {
      setAlertState({
        show: true,
        type: 'error',
        message: 'Preencha nome e preço',
      });
      return;
    }

    const price = parseFloat(priceCoins);
    if (isNaN(price) || price <= 0) {
      setAlertState({
        show: true,
        type: 'error',
        message: 'Preço deve ser um número positivo',
      });
      return;
    }

    try {
      setLoading(true);
      const productData = {
        name: name.trim(),
        priceCoins: price,
        isActive,
      };

      const result = await updateProduct(editingProduct.id, productData);

      setAlertState({
        show: true,
        type: 'success',
        message: `Produto ${result.product.name} atualizado com sucesso!`,
      });

      handleEditCancel();
      await loadProducts();
    } catch (error) {
      setAlertState({
        show: true,
        type: 'error',
        message: getErrorMessage(error),
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (product) => {
    setProductToDelete(product);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!productToDelete) return;

    try {
      setLoading(true);
      await deleteProduct(productToDelete.id);

      setAlertState({
        show: true,
        type: 'success',
        message: `Produto ${productToDelete.name} deletado com sucesso!`,
      });

      setDeleteDialogOpen(false);
      setProductToDelete(null);
      await loadProducts();
    } catch (error) {
      setAlertState({
        show: true,
        type: 'error',
        message: getErrorMessage(error),
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <FormContainer>
            <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <TextField
                label="Nome do Produto"
                value={name}
                onChange={(e) => setName(e.target.value)}
                fullWidth
                required
                variant="outlined"
              />

              <TextField
                label="Preço (R$)"
                type="number"
                value={priceCoins}
                onChange={(e) => setPriceCoins(e.target.value)}
                fullWidth
                required
                variant="outlined"
                inputProps={{ min: 0.01, step: 0.01 }}
                helperText="Preço em reais (ex: 2.50)"
              />

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Chip
                  label={isActive ? 'Ativo' : 'Inativo'}
                  color={isActive ? 'success' : 'default'}
                  onClick={() => setIsActive(!isActive)}
                  sx={{ cursor: 'pointer' }}
                />
              </Box>

              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={loading}
                sx={{
                  bgcolor: '#1e958c',
                  padding: 1.5,
                  fontSize: '16px',
                  fontWeight: 600,
                  '&:hover': { bgcolor: '#0b4f6c' },
                }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Cadastrar Produto'}
              </Button>
            </Box>
          </FormContainer>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormContainer>
            <Box sx={{ mb: 2, fontWeight: 600, color: '#0b4f6c' }}>Produtos Cadastrados</Box>
            {productsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', padding: 4 }}>
                <CircularProgress />
              </Box>
            ) : products.length === 0 ? (
              <Box sx={{ textAlign: 'center', padding: 4, color: '#666' }}>
                Nenhum produto cadastrado
              </Box>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Nome</TableCell>
                      <TableCell align="right">Preço</TableCell>
                      <TableCell align="center">Status</TableCell>
                      <TableCell align="center">Ações</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {products.map((product) => (
                      <TableRow key={product.id}>
                        <TableCell>{product.name}</TableCell>
                        <TableCell align="right">{formatCurrency(product.priceCoins)}</TableCell>
                        <TableCell align="center">
                          <Chip
                            label={product.isActive ? 'Ativo' : 'Inativo'}
                            color={product.isActive ? 'success' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleEditClick(product)}
                            disabled={loading}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteClick(product)}
                            disabled={loading}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </FormContainer>
        </Grid>
      </Grid>

      {/* Dialog de Edição */}
      <Dialog open={editDialogOpen} onClose={handleEditCancel} maxWidth="sm" fullWidth>
        <DialogTitle>Editar Produto</DialogTitle>
        <Box component="form" onSubmit={handleEditSubmit}>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 2 }}>
              <TextField
                label="Nome do Produto"
                value={name}
                onChange={(e) => setName(e.target.value)}
                fullWidth
                required
                variant="outlined"
                autoFocus
              />

              <TextField
                label="Preço (R$)"
                type="number"
                value={priceCoins}
                onChange={(e) => setPriceCoins(e.target.value)}
                fullWidth
                required
                variant="outlined"
                inputProps={{ min: 0.01, step: 0.01 }}
                helperText="Preço em reais (ex: 2.50)"
              />

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Chip
                  label={isActive ? 'Ativo' : 'Inativo'}
                  color={isActive ? 'success' : 'default'}
                  onClick={() => setIsActive(!isActive)}
                  sx={{ cursor: 'pointer' }}
                />
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleEditCancel} disabled={loading}>
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              sx={{
                bgcolor: '#1e958c',
                '&:hover': { bgcolor: '#0b4f6c' },
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Salvar'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      {/* Dialog de Confirmação de Exclusão */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja excluir o produto <strong>{productToDelete?.name}</strong>?
            <br />
            <br />
            Esta ação não pode ser desfeita.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={loading}>
            Cancelar
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Excluir'}
          </Button>
        </DialogActions>
      </Dialog>

      <AlertMessages
        show={alertState.show}
        typeMessage={alertState.type}
        contentMessage={alertState.message}
        setShow={(show) => setAlertState((prev) => ({ ...prev, show }))}
      />
    </>
  );
};
